import type { Assign } from '@ark-ui/react'
import { Field as ArkField } from '@ark-ui/react/field'
import { forwardRef } from 'react'
import { Icon } from '~/components/icon'
import { cn } from '~/utils/cn'
import type { TIconProps } from '~/components/icon'
import type { InputTextSharedProps } from './input-text-root'

export type InputTextHelperTextProps = Assign<
  React.ComponentProps<typeof ArkField.ErrorText>,
  InputTextSharedProps & {
    readOnly?: boolean
    hasError?: boolean
    disabled?: boolean
    /**
     * Se o componente deve renderizar o icone.
     */
    withIcon?: boolean

    /**
     * Icone apresentado no feedback.
     */
    iconName?: TIconProps['symbol']
  }
>

export const InputTextHelperText = forwardRef<
  HTMLDivElement,
  InputTextHelperTextProps
>(
  (
    {
      withIcon,
      iconName = 'rdicon-warning-circle',
      children,
      size = 'ml',
      disabled,
      readOnly,
      hasError,
      ...props
    },
    ref
  ) => {
    if (!children) return null

    const symbol = hasError ? 'rdicon-warning-circle' : iconName
    const displayIcon = hasError || withIcon

    const iconColor: TIconProps['color'] = disabled
      ? 'colorTextNeutralDisabled'
      : readOnly
        ? 'colorTextNeutralReadonly'
        : hasError
          ? 'colorTextDangerAlternative'
          : 'colorTextNeutralAlternative'

    return (
      <div
        ref={ref}
        aria-disabled={disabled}
        className={cn(
          'justify-star flex w-[100%] flex-wrap items-center gap-onepulse px-onepulse text-left font-rdmodern font-semibold text-text-neutral-alternative text-threepulse',
          {
            'text-text-danger-alternative': hasError,
            'cursor-not-allowed': readOnly,
            'cursor-not-allowed text-text-neutral-disabled': disabled,
            'text-text-neutral-readonly': readOnly && !disabled,
          }
        )}
        {...props}
      >
        {displayIcon && (
          <div className="flex items-center">
            <Icon symbol={symbol} color={iconColor} size="tiny" />
          </div>
        )}
        <span className="flex flex-1">{children}</span>
      </div>
    )
  }
)

InputTextHelperText.displayName = 'InputTextHelperText'
